/**

 * @vuelto-surface

 * mobile: ok

 * desktop: ok

 * strategy: unified

 */

import React from 'react';

import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { CASH_MOVEMENT_CATEGORY_LABELS, isCashMovementCategory } from '../infra/cashOutflowCategories';

import type { CashActivityItem } from '../../reports/infra/reportsRemoteRepo';

import { formatRelativeDayTime } from '@shared/vueltoDateTime';

import { spacing } from '../../shared/theme/spacing';

import { ticketColors } from '../../shared/theme/ticketColors'
import { TicketListRow, type TicketListRowGroup } from '../../shared/ticket/TicketListRow';

import { settingsStyles as styles } from '../../settings/screens/settingsScreen.styles';

import { formatMoney } from './workspace/cashManagement.utils';



const KIND_LABELS: Record<CashActivityItem['kind'], string> = {

  cash_open: 'Apertura de caja',

  cash_close: 'Cierre de caja',

  outflow_create: 'Egreso registrado',

  outflow_void: 'Egreso anulado',

};



type Props = {

  items: CashActivityItem[];

  loading?: boolean;

  operatorLabel: (userId: string | null | undefined) => string | null;

  layoutWide?: boolean;

  emptyMessage?: string;

  loadingMessage?: string;

};



function activityMeta(item: CashActivityItem, operatorLabel: string | null): string {

  const parts: string[] = [formatRelativeDayTime(item.occurredAt)];

  if (operatorLabel) parts.push(operatorLabel);

  if (item.category) {

    const label = isCashMovementCategory(item.category)

      ? CASH_MOVEMENT_CATEGORY_LABELS[item.category]

      : item.category;

    parts.push(label);

  }

  if (item.note?.trim()) parts.push(item.note.trim());

  if (item.voidReason?.trim()) parts.push(`Motivo: ${item.voidReason.trim()}`);

  return parts.join(' · ');

}



export function CashActivityRow({

  item,

  operatorLabel,

  group,

}: {

  item: CashActivityItem;

  operatorLabel: (userId: string | null | undefined) => string | null;

  layoutWide?: boolean;

  /** 'grouped'/'groupedLast' para filas dentro de una card flush (proto 1b). */

  group?: TicketListRowGroup;

}) {

  const voided = item.kind === 'outflow_void' || item.status === 'voided';

  const opLabel = operatorLabel(item.actorUserId);

  const title = item.direction === 'in'

    ? item.kind === 'outflow_void'

      ? 'Ingreso anulado'

      : 'Ingreso registrado'

    : KIND_LABELS[item.kind];



  // Badge .aic del proto: candado cerrado/abierto para turno, flechas para

  // movimientos (out = egreso, in = ingreso), undo para anulaciones.

  const leadingIcon =

    item.kind === 'cash_close'

      ? { name: 'lock-closed-outline' as const, tone: 'muted' as const }

      : item.kind === 'cash_open'

        ? { name: 'lock-open-outline' as const, tone: 'brand' as const }

        : voided

          ? { name: 'arrow-undo-outline' as const, tone: 'muted' as const }

          : item.direction === 'in'

            ? { name: 'arrow-down' as const, tone: 'ok' as const }

            : { name: 'arrow-up' as const, tone: 'bad' as const };



  // Signo y tono del monto (proto .aq.bad/.ok): egreso − / ingreso +.

  const signedAmount =

    item.amount != null

      ? item.direction === 'in'

        ? `+${formatMoney(item.amount)}`

        : item.direction === 'out'

          ? `−${formatMoney(Math.abs(item.amount))}`

          : formatMoney(item.amount)

      : null;



  const valueTone =

    item.direction === 'in' ? ('ok' as const) : item.direction === 'out' ? ('bad' as const) : null;



  return (

    <TicketListRow

      title={title}

      meta={activityMeta(item, opLabel)}

      value={signedAmount}

      valueTone={valueTone}

      statusLabel={voided ? 'Anulado' : null}

      statusTone="warn"

      inactive={voided}

      leadingIcon={leadingIcon}

      group={group}

    />

  );

}



export function CashActivityList({

  items,

  loading,

  operatorLabel,

  emptyMessage = 'Sin movimientos en este turno.',

  loadingMessage = 'Cargando movimientos…',

}: Props) {

  if (loading && items.length === 0) {

    return (

      <View style={localStyles.emptyWrap}>

        <ActivityIndicator color={ticketColors.gold} />

        <Text style={styles.caption}>{loadingMessage}</Text>

      </View>

    );

  }



  if (items.length === 0) {

    return (

      <View style={localStyles.emptyWrap}>

        <Text style={styles.caption}>{emptyMessage}</Text>

      </View>

    );

  }



  return (

    <View>

      {items.map((item) => (

        <CashActivityRow

          key={`${item.kind}:${item.id}:${item.occurredAt}`}

          item={item}

          operatorLabel={operatorLabel}

        />

      ))}

    </View>

  );

}



const localStyles = StyleSheet.create({

  emptyWrap: {

    alignItems: 'center',

    justifyContent: 'center',

    paddingVertical: spacing.md,

    gap: spacing.sm,

  },

});


